import React, { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { supabase } from './supabaseClient';
import axios from 'axios';
import Login from './components/Login';
import Register from './components/Register';
import ResetPassword from './components/ResetPassword';
import ResendPassword from './components/ResendPassword';
import Dashboard from './components/Dashboard';
import Classroom from './components/Classroom';
import Navbar from './components/Navbar';
import JoinClassroom from './components/JoinClassroom';
import ErrorBoundary from './components/ErrorBoundary';
import { ConfirmProvider } from './components/ConfirmDialog';
import NotFound from './components/NotFound';
import TutoringRequestsList from './components/admin/TutoringRequestsList';
import TutoringRequestDetail from './components/admin/TutoringRequestDetail';
import TutorApplicationsList from './components/admin/TutorApplicationsList';
import TutorApplicationDetail from './components/admin/TutorApplicationDetail';
import TutorManagementList from './components/admin/TutorManagementList';
import RegistrationLeadsList from './components/admin/RegistrationLeadsList';

// Configure axios base URL for API calls
axios.defaults.baseURL = process.env.REACT_APP_API_URL || '';

// Create Auth Context
const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

// Auth Provider Component
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check active sessions and sets the user
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Listen for changes on auth state
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        // Don't log the user in — let ResetPassword handle it
        return;
      }
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  const value = {
    signUp: (data) => supabase.auth.signUp(data),
    signIn: (data) => supabase.auth.signInWithPassword(data),
    signOut: () => supabase.auth.signOut(),
    user,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user } = useAuth();
  return user ? children : <Navigate to="/login" />;
};

// Main App Component
function App() {
  return (
    <AuthProvider>
      <Router>
        <ErrorBoundary>
        <ConfirmProvider>
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
          <Toaster 
            position="top-right"
            toastOptions={{
              duration: 3000,
              style: {
                background: '#363636',
                color: '#fff',
              },
            }}
          />
          
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/resend-password" element={<ResendPassword />} />

            {/* Protected Routes */}
            <Route path="/" element={
              <ProtectedRoute>
                <div className="flex flex-col h-screen">
                  <Navbar />
                  <Dashboard />
                </div>
              </ProtectedRoute>
            } />
            
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <div className="flex flex-col h-screen">
                  <Navbar />
                  <Dashboard />
                </div>
              </ProtectedRoute>
            } />

            <Route path="/classroom/:id" element={
              <ProtectedRoute>
                <div className="flex flex-col h-screen">
                  <Navbar />
                  <Classroom />
                </div>
              </ProtectedRoute>
            } />

            <Route path="/join" element={
              <ProtectedRoute>
                <div className="flex flex-col h-screen">
                  <Navbar />
                  <JoinClassroom />
                </div>
              </ProtectedRoute>
            } />

            <Route path="/join/:classroomCode" element={
              <ProtectedRoute>
                <div className="flex flex-col h-screen">
                  <Navbar />
                  <JoinClassroom />
                </div>
              </ProtectedRoute>
            } />

            {/* Admin Routes */}
            <Route path="/admin/requests" element={
              <ProtectedRoute>
                <div className="flex flex-col min-h-screen">
                  <Navbar />
                  <TutoringRequestsList />
                </div>
              </ProtectedRoute>
            } />

            <Route path="/admin/requests/:id" element={
              <ProtectedRoute>
                <div className="flex flex-col min-h-screen">
                  <Navbar />
                  <TutoringRequestDetail />
                </div>
              </ProtectedRoute>
            } />

            <Route path="/admin/applications" element={
              <ProtectedRoute>
                <div className="flex flex-col min-h-screen">
                  <Navbar />
                  <TutorApplicationsList />
                </div>
              </ProtectedRoute>
            } />

            <Route path="/admin/applications/:id" element={
              <ProtectedRoute>
                <div className="flex flex-col min-h-screen">
                  <Navbar />
                  <TutorApplicationDetail />
                </div>
              </ProtectedRoute>
            } />

            <Route path="/admin/tutors" element={
              <ProtectedRoute>
                <div className="flex flex-col min-h-screen">
                  <Navbar />
                  <TutorManagementList />
                </div>
              </ProtectedRoute>
            } />

            <Route path="/admin/leads" element={
              <ProtectedRoute>
                <div className="flex flex-col min-h-screen">
                  <Navbar />
                  <RegistrationLeadsList />
                </div>
              </ProtectedRoute>
            } />

            {/* 404 Not Found */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </div>
        </ConfirmProvider>
        </ErrorBoundary>
      </Router>
    </AuthProvider>
  );
}

export default App;
